// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { SessionStorageKeys } from '@common/utils/sessionStorage';
import { Alert } from '@imprivata-cloud/design-system';
import { useMfaEnrollmentStatus } from '@portal/hooks/useMfaEnrollmentStatus';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootRoutes } from '@/index';
import './MfaEnrollmentBanner.less';

const WARNING_ROOT = 'portal.landing.mfa-enrollment-banner';
const BLOCKER_ROOT = 'portal.landing.mfa-restricted-view';

export const MfaEnrollmentBanner = ({
  onDismiss,
  hideCta = false,
}: { onDismiss?: () => void; hideCta?: boolean } = {}) => {
  const status = useMfaEnrollmentStatus();
  const { t, i18n } = useTranslation();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SessionStorageKeys.MFA_ENROLLMENT_BANNER_DISMISSED) === 'true',
  );

  // Render the expiry instant in the browser's local timezone so the user sees
  // a date and time that match their wall clock. Intl.DateTimeFormat resolves
  // DST automatically. The underlying instant is UTC midnight at the start of
  // the day after the deadline (see getMfaEnrollmentStatus), so this is purely
  // a display projection.
  //
  // Time is only shown in hours/minutes mode, so the time formatter work is
  // skipped entirely in the common case where daysRemaining >= 1. Minutes are
  // omitted when the local time falls on a whole hour (the common case, since
  // expiresAt is at UTC midnight and most offsets are whole hours).
  const formattedDeadline = useMemo(() => {
    if (!status) {
      return { date: '', time: '' };
    }

    const date = new Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    }).format(status.expiresAt);
    if (status.daysRemaining >= 1) {
      return { date, time: '' };
    }

    const withMinutes = new Intl.DateTimeFormat(i18n.language, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const minutePart = withMinutes.formatToParts(status.expiresAt).find(p => p.type === 'minute');
    const time =
      minutePart?.value === '00'
        ? new Intl.DateTimeFormat(i18n.language, { hour: 'numeric' }).format(status.expiresAt)
        : withMinutes.format(status.expiresAt);
    return { date, time };
  }, [status, i18n.language]);

  if (!status) {
    return null;
  }

  const ctaLink = (root: string) => (
    <a
      href={RootRoutes.SELF_SERVICE}
      target="_blank"
      rel="noopener noreferrer"
      className="mfa-enrollment-action-link"
    >
      {t(`${root}.action`)}
    </a>
  );

  const containerClass = `mfa-enrollment-banner-container${hideCta ? ' mfa-enrollment-banner-container--no-cta' : ''}`;

  if (status.isPastDeadline) {
    return (
      <div className={containerClass}>
        <Alert
          status="error"
          title={t(`${BLOCKER_ROOT}.title`)}
          message={t(`${BLOCKER_ROOT}.message`)}
          actions={hideCta ? undefined : ctaLink(BLOCKER_ROOT)}
        />
      </div>
    );
  }

  if (dismissed) {
    return null;
  }

  // Switch from days to hours once we drop below a full UTC day so the user
  // gets a finer-grained countdown as the deadline approaches. hoursRemaining
  // is floored, so 0 means "between 0 and 60 minutes remain" — render that as
  // "x minutes remaining" using minutesRemaining.
  const useHoursMode = status.daysRemaining < 1;
  const lessThanHourMode = useHoursMode && status.hoursRemaining === 0;
  const hoursOrDaysContext = useHoursMode ? 'hours' : 'days';
  const deadlineContext = lessThanHourMode ? 'lessThanHour' : hoursOrDaysContext;
  const hoursOrDaysCount = useHoursMode ? status.hoursRemaining : status.daysRemaining;
  const deadlineCount = lessThanHourMode ? status.minutesRemaining : hoursOrDaysCount;
  const deadlineText = t(`${WARNING_ROOT}.deadline`, {
    context: deadlineContext,
    date: formattedDeadline.date,
    time: formattedDeadline.time,
    count: deadlineCount,
  });
  // The design-system Alert types `message` as string, but renders it as JSX
  // at runtime so ReactNode content works fine. The cast suppresses the false
  // positive without changing runtime behavior.
  const warningMessage = (
    <>
      {t(`${WARNING_ROOT}.message`)}{' '}
      <span className="mfa-enrollment-deadline">
        <span>{t(`${WARNING_ROOT}.deadline_prefix`)}</span>
        <strong>{deadlineText}</strong>
      </span>
    </>
  ) as unknown as string;
  return (
    <div className={containerClass}>
      <Alert
        status="warning"
        title={t(`${WARNING_ROOT}.title`)}
        message={warningMessage}
        actions={hideCta ? undefined : ctaLink(WARNING_ROOT)}
        onClose={() => {
          setDismissed(true);
          sessionStorage.setItem(SessionStorageKeys.MFA_ENROLLMENT_BANNER_DISMISSED, 'true');
          onDismiss?.();
        }}
      />
    </div>
  );
};
